import { Skeleton, SkeletonItem } from '@fluentui/react-components';
import { cn } from '@/lib/cn';

/**
 * Loading placeholders.
 *
 * They mirror the real layout (same paddings, same grid, same row heights) so
 * the content swap never produces a layout shift.
 */

interface SkeletonProps {
  className?: string;
}

/* -------------------------------------------------------------------------- */
/* Calendar                                                                    */
/* -------------------------------------------------------------------------- */

export function SkeletonCalendar({
  rooms = 3,
  slots = 10,
  className,
}: SkeletonProps & { rooms?: number; slots?: number }): JSX.Element {
  return (
    <Skeleton
      aria-label="Cargando calendario"
      className={cn('surface-card overflow-hidden', className)}
    >
      {/* Column headers: one per room. */}
      <div
        className="grid gap-px border-b border-edge bg-edge/60"
        style={{ gridTemplateColumns: `72px repeat(${rooms}, minmax(0, 1fr))` }}
      >
        <div className="bg-card p-3" />
        {Array.from({ length: rooms }, (_, index) => (
          <div key={`head-${index}`} className="flex items-center gap-2 bg-card p-3">
            <SkeletonItem shape="square" size={28} />
            <div className="flex-1 space-y-1.5">
              <SkeletonItem size={12} style={{ width: '70%' }} />
              <SkeletonItem size={8} style={{ width: '45%' }} />
            </div>
          </div>
        ))}
      </div>

      {/* Time grid. */}
      <div
        className="grid gap-px bg-edge/40"
        style={{ gridTemplateColumns: `72px repeat(${rooms}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: slots }, (_, row) => (
          <div key={`row-${row}`} className="contents">
            <div className="flex items-start justify-end bg-card px-3 py-4">
              <SkeletonItem size={8} style={{ width: 34 }} />
            </div>
            {Array.from({ length: rooms }, (_, column) => (
              <div key={`cell-${row}-${column}`} className="bg-card p-2">
                {(row + column) % 3 === 0 ? (
                  <SkeletonItem size={40} style={{ borderRadius: 'var(--radius-lg)' }} />
                ) : (
                  <div className="h-10" />
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </Skeleton>
  );
}

/* -------------------------------------------------------------------------- */
/* Room cards                                                                  */
/* -------------------------------------------------------------------------- */

export function SkeletonRoomCards({
  count = 6,
  className,
}: SkeletonProps & { count?: number }): JSX.Element {
  return (
    <Skeleton
      aria-label="Cargando salas"
      className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3', className)}
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={`room-${index}`} className="surface-card overflow-hidden">
          {/* 2.5:1, la misma proporción que la portada real: un marcador más
              bajo produce un salto al cargar justo donde la vista debería
              estabilizarse. */}
          <SkeletonItem
            size={96}
            className="aspect-[5/2] w-full"
            style={{ borderRadius: 0, height: 'auto' }}
          />
          <div className="space-y-3 p-4">
            <div className="flex items-center gap-3">
              <SkeletonItem shape="square" size={40} />
              <div className="flex-1 space-y-2">
                <SkeletonItem size={16} style={{ width: '65%' }} />
                <SkeletonItem size={8} style={{ width: '40%' }} />
              </div>
            </div>
            <div className="flex gap-2">
              <SkeletonItem size={20} style={{ width: 72, borderRadius: 'var(--radius-pill)' }} />
              <SkeletonItem size={20} style={{ width: 56, borderRadius: 'var(--radius-pill)' }} />
              <SkeletonItem size={20} style={{ width: 88, borderRadius: 'var(--radius-pill)' }} />
            </div>
            <SkeletonItem size={8} style={{ width: '80%' }} />
          </div>
        </div>
      ))}
    </Skeleton>
  );
}

/* -------------------------------------------------------------------------- */
/* Table                                                                       */
/* -------------------------------------------------------------------------- */

export function SkeletonTable({
  rows = 8,
  columns = 5,
  withAvatar = true,
  className,
}: SkeletonProps & { rows?: number; columns?: number; withAvatar?: boolean }): JSX.Element {
  return (
    <Skeleton
      aria-label="Cargando información"
      className={cn('surface-card overflow-hidden', className)}
    >
      <div
        className="grid items-center gap-4 border-b border-edge bg-subtle px-4 py-3"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: columns }, (_, index) => (
          <SkeletonItem key={`th-${index}`} size={8} style={{ width: '60%' }} />
        ))}
      </div>

      <div className="divide-y divide-edge">
        {Array.from({ length: rows }, (_, row) => (
          <div
            key={`tr-${row}`}
            className="grid items-center gap-4 px-4 py-3"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            <div className="flex items-center gap-3">
              {withAvatar ? <SkeletonItem shape="circle" size={32} /> : null}
              <SkeletonItem size={12} style={{ width: '70%' }} />
            </div>
            {Array.from({ length: columns - 1 }, (_, column) => (
              <SkeletonItem
                key={`td-${row}-${column}`}
                size={12}
                style={{ width: `${45 + ((row + column) % 4) * 12}%` }}
              />
            ))}
          </div>
        ))}
      </div>
    </Skeleton>
  );
}

/* -------------------------------------------------------------------------- */
/* Page / route level                                                          */
/* -------------------------------------------------------------------------- */

/** Route level fallback used by the router `<Suspense>` boundary. */
export function SkeletonPage({ className }: SkeletonProps): JSX.Element {
  return (
    <Skeleton
      aria-label="Cargando la vista"
      className={cn('mx-auto w-full max-w-content space-y-6 p-4 md:p-6', className)}
    >
      <div className="space-y-3">
        <SkeletonItem size={24} style={{ width: 260 }} />
        <SkeletonItem size={12} style={{ width: 420, maxWidth: '80%' }} />
      </div>
      <SkeletonRoomCards count={3} />
    </Skeleton>
  );
}

/** Inline placeholder for lists such as "Mis reservaciones". */
export function SkeletonList({
  count = 4,
  className,
}: SkeletonProps & { count?: number }): JSX.Element {
  return (
    <Skeleton aria-label="Cargando reservaciones" className={cn('space-y-3', className)}>
      {Array.from({ length: count }, (_, index) => (
        <div key={`item-${index}`} className="surface-card flex items-center gap-4 p-4">
          <SkeletonItem shape="square" size={40} />
          <div className="flex-1 space-y-2">
            <SkeletonItem size={12} style={{ width: '45%' }} />
            <SkeletonItem size={8} style={{ width: '30%' }} />
          </div>
          <SkeletonItem size={24} style={{ width: 96, borderRadius: 'var(--radius-pill)' }} />
        </div>
      ))}
    </Skeleton>
  );
}
